import React, { useState, useEffect, useMemo } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import * as XLSX from "xlsx";
import Select from "react-select";

// Font Awesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileCsv,
  faFileExcel,
  faPenToSquare,
  faAnglesLeft,
  faAngleLeft,
  faAngleRight,
  faAnglesRight,
} from "@fortawesome/free-solid-svg-icons";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const edificioIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const cierreIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const IntervencionesGerente = () => {
  const { auth } = useAuth();

  // Datos base
  const [ticajesRaw, setTicajesRaw] = useState([]);
  const [ticajes, setTicajes] = useState([]); // enriquecidos
  const [usuariosEquipo, setUsuariosEquipo] = useState([]); // solo usuarios a cargo del gerente
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);

  // Filtros
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("");
  const [filtroOrden, setFiltroOrden] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // Ordenación y UI
  const [orden, setOrden] = useState({ campo: "inicio", ascendente: true });
  const [mensaje, setMensaje] = useState("");

  // Mapa
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio"); // "inicio" | "cierre"
  const [ubicacionInicio, setUbicacionInicio] = useState(null);   // {lat,lng}
  const [ubicacionCierre, setUbicacionCierre] = useState(null);   // {lat,lng}
  const [ubicacionCentro, setUbicacionCentro] = useState(null);   // {lat,lng}

  // Modal edición
  const [modalEditar, setModalEditar] = useState(false);
  const [ticajeEditar, setTicajeEditar] = useState(null);
  const [editForm, setEditForm] = useState({
    inicio: "", fin: "", motivo_fin: "", observaciones_admin: "", comentarios: ""
  });
  const [editError, setEditError] = useState("");

  // Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // --- Carga paralela de catálogos + ticajes
  useEffect(() => {
    const cargarTodo = async () => {
      try {
        const [resT, resU, resC, resCentros, resOts] = await Promise.all([
          fetch(`${Global.url}ticajes/finalizados`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}usuarios`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}clientes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}centros/todos`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}ordenes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        const datosT = resT.ok ? await resT.json() : [];
        const datosU = resU.ok ? await resU.json() : [];
        const datosC = resC.ok ? await resC.json() : [];
        const datosCentros = resCentros.ok ? await resCentros.json() : [];
        const datosOts = resOts.ok ? await resOts.json() : [];

        // Usuarios a cargo del gerente
        const equipo = (Array.isArray(datosU) ? datosU : []).filter(u => u.gerente_id === auth?.id);
        setUsuariosEquipo(equipo);
        setClientes(Array.isArray(datosC) ? datosC : []);
        setCentros(Array.isArray(datosCentros) ? datosCentros : []);
        setOrdenes(Array.isArray(datosOts) ? datosOts : []);

        // Normalización base
        const normalizados = (Array.isArray(datosT) ? datosT : []).map((t) => ({
          ...t,
          estado: t.motivo_fin?.toLowerCase().includes("cancel")
            ? "Cancelado"
            : t.motivo_fin?.toLowerCase().includes("manual")
            ? "Finalizado Manual"
            : "Finalizado",
          duracionMs: new Date(t.fin) - new Date(t.inicio),
        }));
        setTicajesRaw(normalizados);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setMensaje("No se pudo conectar con el servidor.");
      }
    };

    if (auth?.id) cargarTodo();
  }, [auth]);

  // Maps para nombres
  const mapUsuario = useMemo(() => {
    const m = {};
    for (const u of usuariosEquipo) {
      const full = [u.nombre, u.apellidos].filter(Boolean).join(" ");
      m[u.id] = full || u.nombre || `Usuario ${u.id}`;
    }
    return m;
  }, [usuariosEquipo]);

  const mapCliente = useMemo(() => {
    const m = {};
    for (const c of clientes) m[c.id] = c.nombre;
    return m;
  }, [clientes]);

  const mapCentro = useMemo(() => {
    const m = {};
    for (const c of centros) m[c.id] = c.nombre;
    return m;
  }, [centros]);

  const mapOrden = useMemo(() => {
    const m = {};
    for (const o of ordenes) m[o.id] = o.titulo || `OT ${o.id}`;
    return m;
  }, [ordenes]);

  // Enriquecer ticajes (y restringir a equipo del gerente)
  useEffect(() => {
    const idsEquipo = new Set(usuariosEquipo.map(u => u.id));
    const enriquecidos = ticajesRaw
      .filter(t => idsEquipo.has(t.usuario_id)) // Solo equipo del gerente
      .map((t) => {
        const u = usuariosEquipo.find(u => u.id === t.usuario_id);
        return {
          ...t,
          usuario: u ? `${u.nombre} ${u.apellidos || ""}`.trim()
                     : (t.usuario || t.usuario_id || "-"),
          cliente: t.cliente || (mapCliente[t.cliente_id] ?? t.cliente_id),
          centro: t.centro || (mapCentro[t.centro_id] ?? t.centro_id),
          orden_nombre: t.orden_nombre || (t.orden_id ? (mapOrden[t.orden_id] ?? `OT ${t.orden_id}`) : "-"),
          modificado_por_nombre:
            t.creado_por_admin_id ? (mapUsuario[t.creado_por_admin_id] || "-")
                                  : (t.creado_por_nombre || "-"),
        };
      });
    setTicajes(enriquecidos);
  }, [ticajesRaw, usuariosEquipo, mapCliente, mapCentro, mapOrden, mapUsuario]);

  // Opciones de filtros (react-select)
  const usuarioOptions = useMemo(() => ([
    { value: "", label: "Todos" },
    ...usuariosEquipo
      .slice()
      .sort((a, b) => (a.nombre + (a.apellidos || "")).localeCompare(b.nombre + (b.apellidos || "")))
      .map(u => ({ value: u.id, label: `${u.nombre} ${u.apellidos || ""}`.trim() }))
  ]), [usuariosEquipo]);

  const clienteOptions = useMemo(() => ([
    { value: "", label: "Todos" },
    ...clientes
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map(c => ({ value: c.id, label: c.nombre }))
  ]), [clientes]);

  const centroOptions = useMemo(() => ([
    { value: "", label: "Todos" },
    ...centros
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map(c => ({ value: c.id, label: c.nombre }))
  ]), [centros]);

  const ordenOptions = useMemo(() => ([
    { value: "", label: "Todas" },
    ...ordenes
      .slice()
      .sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""))
      .map(o => ({ value: o.id, label: o.titulo || `OT ${o.id}` }))
  ]), [ordenes]);

  // Filtrado por selects y fechas
  const ticajesFiltrados = useMemo(() => {
    return ticajes.filter((t) => {
      const inicioISO = new Date(t.inicio).toISOString().split("T")[0];

      const matchUsuario = !filtroUsuario || String(t.usuario_id ?? "") === String(filtroUsuario);
      const matchCliente = !filtroCliente || String(t.cliente_id ?? "") === String(filtroCliente);
      const matchCentro  = !filtroCentro  || String(t.centro_id  ?? "") === String(filtroCentro);
      const matchOrden   = !filtroOrden   || String(t.orden_id   ?? "") === String(filtroOrden);

      const matchFecha =
        !fechaDesde && !fechaHasta
          ? true
          : fechaDesde && !fechaHasta
          ? inicioISO === fechaDesde
          : fechaDesde && fechaHasta
          ? inicioISO >= fechaDesde && inicioISO <= fechaHasta
          : true;

      return matchUsuario && matchCliente && matchCentro && matchOrden && matchFecha;
    });
  }, [ticajes, filtroUsuario, filtroCliente, filtroCentro, filtroOrden, fechaDesde, fechaHasta]);

  // Ordenación (incluimos campos de cierre)
  const manejarOrden = (campo) => {
    const normalizar = {
      duracion: "duracionMs",
      usuario: "usuario",
      cliente: "cliente",
      centro: "centro",
      estado: "estado",
      en_rango: "en_rango",
      en_rango_cierre: "en_rango_cierre",
      distancia_m: "distancia_m",
      distancia_m_cierre: "distancia_m_cierre",
      comentarios: "comentarios",
      modificado_por_nombre: "modificado_por_nombre",
      inicio: "inicio",
      fin: "fin",
    }[campo] || campo;

    setOrden((prev) => ({
      campo: normalizar,
      ascendente: prev.campo === normalizar ? !prev.ascendente : true,
    }));
  };

  const ticajesOrdenados = useMemo(() => {
    return [...ticajesFiltrados].sort((a, b) => {
      let valorA = a[orden.campo] ?? "";
      let valorB = b[orden.campo] ?? "";

      if (orden.campo === "inicio" || orden.campo === "fin") {
        valorA = new Date(valorA);
        valorB = new Date(valorB);
      }

      if (typeof valorA === "string" && typeof valorB === "string") {
        return orden.ascendente
          ? valorA.localeCompare(valorB, undefined, { sensitivity: "base" })
          : valorB.localeCompare(valorA, undefined, { sensitivity: "base" });
      }

      return orden.ascendente ? (valorA - valorB) : (valorB - valorA);
    });
  }, [ticajesFiltrados, orden]);

  // Paginación (reinicia a 1 al cambiar filtros/orden/tamaño)
  useEffect(() => {
    setPage(1);
  }, [filtroUsuario, filtroCliente, filtroCentro, filtroOrden, fechaDesde, fechaHasta, pageSize, orden]);

  const total = ticajesOrdenados.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageClamped = Math.min(page, totalPages);
  const startIndex = (pageClamped - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const pageItems = useMemo(
    () => ticajesOrdenados.slice(startIndex, endIndex),
    [ticajesOrdenados, startIndex, endIndex]
  );

  // Utilidades
  const calcularDuracionMs = (ms) => {
    if (!ms || isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  function toDatetimeLocalString(date) {
    if (!date) return "";
    const pad = (n) => n.toString().padStart(2, "0");
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  // Exportaciones (añadimos cierre)
  const exportarCSVReducido = (datos, nombreArchivo = "ticajes.csv") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }
    const encabezados = [
      "Usuario", "Cliente", "Centro",
      "Inicio", "Fin", "Duración", "Estado",
      "En Rango (Inicio)", "Distancia (Inicio m)",
      "En Rango (Cierre)", "Distancia (Cierre m)",
      "Comentarios", "Modificado por", "Observaciones"
    ];
    const csvRows = [
      encabezados.join(","),
      ...datos.map((t) => {
        const fila = [
          t.usuario ?? "",
          t.cliente ?? "",
          t.centro ?? "",
          t.inicio ? new Date(t.inicio).toLocaleString() : "-",
          t.fin ? new Date(t.fin).toLocaleString() : "-",
          calcularDuracionMs(t.duracionMs),
          t.estado,
          t.en_rango ? "En rango" : "Fuera de rango",
          t.distancia_m != null ? t.distancia_m : "",
          (t.en_rango_cierre == null ? "" : (t.en_rango_cierre ? "En rango" : "Fuera de rango")),
          (t.distancia_m_cierre != null ? t.distancia_m_cierre : ""),
          t.comentarios ?? "Sin comentarios",
          t.modificado_por_nombre || "-",
          t.observaciones_admin || t.comentario_admin || "-"
        ];
        return fila.map((val) => `"${(val ?? "").toString().replace(/"/g, '""')}"`).join(",");
      }),
    ];
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nombreArchivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportarExcel = (datos, nombreArchivo = "ticajes.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      alert("No hay datos para exportar.");
      return;
    }
    const datosFormatoExcel = datos.map((t) => ({
      Usuario: t.usuario ?? "",
      Cliente: t.cliente ?? "",
      Centro: t.centro ?? "",
      Inicio: t.inicio ? new Date(t.inicio).toLocaleString() : "-",
      Fin: t.fin ? new Date(t.fin).toLocaleString() : "-",
      Duración: calcularDuracionMs(t.duracionMs),
      Estado: t.estado,
      "En Rango (Inicio)": t.en_rango ? "En rango" : "Fuera de rango",
      "Distancia (Inicio m)": t.distancia_m != null ? t.distancia_m : "",
      "En Rango (Cierre)": (t.en_rango_cierre == null ? "" : (t.en_rango_cierre ? "En rango" : "Fuera de rango")),
      "Distancia (Cierre m)": t.distancia_m_cierre != null ? t.distancia_m_cierre : "",
      Comentarios: t.comentarios ?? "Sin comentarios",
      "Modificado por": t.modificado_por_nombre || "-",
      Observaciones: t.observaciones_admin || t.comentario_admin || "-"
    }));
    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Ticajes");
    XLSX.writeFile(libro, nombreArchivo);
  };

  // Mapa: inicio + cierre + centro
  const abrirMapa = async (t, focus = "inicio") => {
    setMapFocus(focus);

    const ini = (t.latitud && t.longitud)
      ? { lat: Number(t.latitud), lng: Number(t.longitud) }
      : null;

    const fin = (t.latitud_cierre && t.longitud_cierre)
      ? { lat: Number(t.latitud_cierre), lng: Number(t.longitud_cierre) }
      : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${t.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      if (res.ok && data.latitud && data.longitud) {
        setUbicacionCentro({
          lat: parseFloat(data.latitud),
          lng: parseFloat(data.longitud),
        });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }

    setMostrarMapa(true);
  };

  // Modal edición
  const abrirModalEditar = (ticaje) => {
    setTicajeEditar(ticaje);
    setEditForm({
      inicio: ticaje.inicio ? toDatetimeLocalString(new Date(ticaje.inicio)) : "",
      fin: ticaje.fin ? toDatetimeLocalString(new Date(ticaje.fin)) : "",
      motivo_fin: ticaje.motivo_fin || "",
      observaciones_admin: ticaje.observaciones_admin || ticaje.comentario_admin || "",
      comentarios: ticaje.comentarios || "",
    });
    setModalEditar(true);
    setEditError("");
  };

  const cerrarModalEditar = () => {
    setModalEditar(false);
    setTicajeEditar(null);
    setEditForm({ inicio: "", fin: "", motivo_fin: "", observaciones_admin: "", comentarios: "" });
    setEditError("");
  };

  const handleSubmitEditar = async (e) => {
    e.preventDefault();
    setEditError("");

    if (!editForm.inicio || !editForm.fin) {
      setEditError("Inicio y fin son obligatorios");
      return;
    }
    const dInicio = new Date(editForm.inicio);
    const dFin = new Date(editForm.fin);
    if (isNaN(dInicio) || isNaN(dFin) || dFin < dInicio) {
      setEditError("La fecha de fin debe ser posterior al inicio");
      return;
    }

    try {
      const res = await fetch(`${Global.url}ticajes/modificar/${ticajeEditar.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          inicio: editForm.inicio,
          fin: editForm.fin,
          motivo_fin: editForm.motivo_fin,
          observaciones_admin: editForm.observaciones_admin,
          comentarios: editForm.comentarios,
        }),
      });

      if (res.ok) {
        cerrarModalEditar();
        // refrescar ticajes base
        const resT = await fetch(`${Global.url}ticajes/finalizados`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const datosT = resT.ok ? await resT.json() : [];
        const normalizados = (Array.isArray(datosT) ? datosT : []).map((t) => ({
          ...t,
          estado: t.motivo_fin?.toLowerCase().includes("cancel")
            ? "Cancelado"
            : t.motivo_fin?.toLowerCase().includes("manual")
            ? "Finalizado Manual"
            : "Finalizado",
          duracionMs: new Date(t.fin) - new Date(t.inicio),
        }));
        setTicajesRaw(normalizados);
      } else {
        const data = await res.json();
        setEditError(data.message || "Error al modificar la intervención");
      }
    } catch (err) {
      setEditError("Error de conexión con el servidor");
    }
  };

  const limpiarFiltros = () => {
    setFiltroUsuario("");
    setFiltroCliente("");
    setFiltroCentro("");
    setFiltroOrden("");
    setFechaDesde("");
    setFechaHasta("");
  };

  // ---------------- RENDER -------------------
  return (
    <div className="consulta-ticajes">
      <h2 className="centrar-titulo">Ticajes de tu equipo</h2>
      {mensaje && <p className="rojo">{mensaje}</p>}

      <div className="filtros">
        <div className="filtro">
          <label>Usuario:</label>
          <Select
            options={usuarioOptions}
            value={
              filtroUsuario
                ? {
                    value: filtroUsuario,
                    label: (() => {
                      const u = usuariosEquipo.find(u => u.id === parseInt(filtroUsuario));
                      return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : "";
                    })(),
                  }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroUsuario(selected?.value || "")}
            isClearable
            placeholder="Filtrar por usuario"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Cliente:</label>
          <Select
            options={clienteOptions}
            value={
              filtroCliente
                ? {
                    value: filtroCliente,
                    label: (() => {
                      const c = clientes.find(c => c.id === parseInt(filtroCliente));
                      return c ? c.nombre : "";
                    })(),
                  }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroCliente(selected?.value || "")}
            isClearable
            placeholder="Filtrar por cliente"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Centro:</label>
          <Select
            options={centroOptions}
            value={
              filtroCentro
                ? {
                    value: filtroCentro,
                    label: (() => {
                      const c = centros.find(c => c.id === parseInt(filtroCentro));
                      return c ? c.nombre : `Centro ${filtroCentro}`;
                    })(),
                  }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroCentro(selected?.value || "")}
            isClearable
            placeholder="Filtrar por centro"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>OT:</label>
          <Select
            options={ordenOptions}
            value={
              filtroOrden
                ? {
                    value: filtroOrden,
                    label: (() => {
                      const o = ordenes.find(o => o.id === parseInt(filtroOrden));
                      return o ? (o.titulo || `OT ${o.id}`) : `OT ${filtroOrden}`;
                    })(),
                  }
                : { value: "", label: "Todas" }
            }
            onChange={(selected) => setFiltroOrden(selected?.value || "")}
            isClearable
            placeholder="Filtrar por OT"
            classNamePrefix="select"
          />
        </div>

        <div className="filtro">
          <label>Desde:</label>
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
        </div>

        <div className="filtro">
          <label>Hasta:</label>
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
        </div>

        <div className="filtro" style={{ alignSelf: "end" }}>
          <button className="btn-login" onClick={limpiarFiltros}>Limpiar filtros</button>
        </div>
      </div>

      {/* Paginación superior */}
      <div className="paginacion-bar">
        <div className="paginacion-info">
          Mostrando <strong>{total === 0 ? 0 : startIndex + 1}</strong>–<strong>{endIndex}</strong> de <strong>{total}</strong>
        </div>
        <div className="paginacion-controles">
          <button className="btn-mapa" onClick={() => setPage(1)} disabled={pageClamped === 1} title="Primera">
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
          <button className="btn-mapa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageClamped === 1} title="Anterior">
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <span className="paginacion-estado">Página {pageClamped} / {totalPages}</span>
          <button className="btn-mapa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageClamped === totalPages} title="Siguiente">
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
          <button className="btn-mapa" onClick={() => setPage(totalPages)} disabled={pageClamped === totalPages} title="Última">
            <FontAwesomeIcon icon={faAnglesRight} />
          </button>

          <select
            className="paginacion-size"
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            title="Tamaño de página"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} / pág.</option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-listado-padre">
        <table className="table-listado">
          <thead>
            <tr>
              <th onClick={() => manejarOrden("usuario")}>Usuario</th>
              <th onClick={() => manejarOrden("cliente")}>Cliente</th>
              <th onClick={() => manejarOrden("centro")}>Centro</th>
              <th onClick={() => manejarOrden("inicio")}>Inicio</th>
              <th onClick={() => manejarOrden("fin")}>Fin</th>
              <th onClick={() => manejarOrden("duracion")}>Duración</th>
              <th onClick={() => manejarOrden("estado")}>Estado</th>

              <th className="col-estado">
                <div className="th-multi">
                  <span onClick={() => manejarOrden("en_rango")} className="ordenable">En Rango (Inicio)</span>
                  <span onClick={() => manejarOrden("en_rango_cierre")} className="ordenable th-sub">En Rango (Cierre)</span>
                </div>
              </th>

              <th className="col-distancia">
                <div className="th-multi">
                  <span onClick={() => manejarOrden("distancia_m")} className="ordenable">Distancia (Inicio)</span>
                  <span onClick={() => manejarOrden("distancia_m_cierre")} className="ordenable th-sub">Distancia (Cierre)</span>
                </div>
              </th>

              <th onClick={() => manejarOrden("comentarios")}>Comentarios</th>
              <th onClick={() => manejarOrden("modificado_por_nombre")}>Modificado por</th>
              <th onClick={() => manejarOrden("observaciones_admin")}>Observaciones</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {pageItems.map((t) => (
              <tr key={`${t.id}-${t.estado}`}>
                <td>{t.usuario}</td>
                <td>{t.cliente}</td>
                <td>{t.centro}</td>
                <td>{new Date(t.inicio).toLocaleString()}</td>
                <td>{t.fin ? new Date(t.fin).toLocaleString() : "-"}</td>
                <td>{calcularDuracionMs(t.duracionMs)}</td>
                <td>{t.estado}</td>

                {/* Estado inicio/cierre */}
                <td className="col-estado">
                  <div className="estado-cell">
                    <span className={`estado estado-inicio ${t.en_rango ? "ok" : "ko"}`}>
                      Inicio: {t.en_rango ? "En rango" : "Fuera de rango"}
                    </span>
                    <span className={`estado estado-cierre ${
                      t.en_rango_cierre == null ? "muted" : (t.en_rango_cierre ? "ok" : "ko")
                    }`}>
                      Cierre: {t.en_rango_cierre == null ? "-" : (t.en_rango_cierre ? "En rango" : "Fuera de rango")}
                    </span>
                  </div>
                </td>

                {/* Distancias inicio/cierre con botón de mapa */}
                <td className="col-distancia">
                  <div className="distancia-cell">
                    {t.latitud && t.longitud && t.distancia_m != null ? (
                      <button
                        className="btn-mapa"
                        onClick={() => abrirMapa(t, "inicio")}
                        title="Ver mapa (inicio)"
                      >
                        Inicio: {t.distancia_m} m
                      </button>
                    ) : (
                      <span className="distancia-placeholder">Inicio: -</span>
                    )}

                    {t.latitud_cierre && t.longitud_cierre && t.distancia_m_cierre != null ? (
                      <button
                        className="btn-mapa"
                        onClick={() => abrirMapa(t, "cierre")}
                        title="Ver mapa (cierre)"
                      >
                        Cierre: {t.distancia_m_cierre} m
                      </button>
                    ) : (
                      <span className="distancia-placeholder">Cierre: -</span>
                    )}
                  </div>
                </td>

                <td>{t.comentarios || "Sin comentarios"}</td>
                <td>{t.modificado_por_nombre || "-"}</td>
                <td>{t.observaciones_admin || "-"}</td>
                <td>
                  <button className="btn-mapa" title="Editar intervención" onClick={() => abrirModalEditar(t)}>
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                </td>
              </tr>
            ))}
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={13} className="no-results">
                  No hay resultados con los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Exportaciones */}
      <div className="export-bar">
        <button className="btn-mapa" onClick={() => exportarCSVReducido(ticajes, "todos_ticajes.csv")}>
          <FontAwesomeIcon icon={faFileCsv} className="btn-icon-left" /> CSV Todos
        </button>
        <button className="btn-mapa" onClick={() => exportarCSVReducido(ticajesOrdenados, "ticajes_filtrados.csv")}>
          <FontAwesomeIcon icon={faFileCsv} className="btn-icon-left" /> CSV Filtrados
        </button>
        <button className="btn-mapa" onClick={() => exportarExcel(ticajes, "todos_ticajes.xlsx")}>
          <FontAwesomeIcon icon={faFileExcel} className="btn-icon-left" /> Excel Todos
        </button>
        <button className="btn-mapa" onClick={() => exportarExcel(ticajesOrdenados, "ticajes_filtrados.xlsx")}>
          <FontAwesomeIcon icon={faFileExcel} className="btn-icon-left" /> Excel Filtrados
        </button>
      </div>

      {/* MODAL EDICIÓN */}
      {modalEditar && (
        <div className="turnos-modal-backdrop">
          <div className="turnos-modal">
            <h3 style={{ marginBottom: "0.7rem" }}>Editar Intervención</h3>
            <form onSubmit={handleSubmitEditar}>
              <label>
                Inicio:
                <input
                  type="datetime-local"
                  name="inicio"
                  value={editForm.inicio}
                  onChange={(e) => setEditForm({ ...editForm, inicio: e.target.value })}
                  required
                />
              </label>
              <label>
                Fin:
                <input
                  type="datetime-local"
                  name="fin"
                  value={editForm.fin}
                  onChange={(e) => setEditForm({ ...editForm, fin: e.target.value })}
                  required
                />
              </label>
              <label>
                Motivo Fin:
                <input
                  name="motivo_fin"
                  value={editForm.motivo_fin}
                  onChange={(e) => setEditForm({ ...editForm, motivo_fin: e.target.value })}
                  placeholder="Motivo fin"
                />
              </label>
              <label>
                Observaciones:
                <textarea
                  name="observaciones_admin"
                  value={editForm.observaciones_admin}
                  onChange={(e) => setEditForm({ ...editForm, observaciones_admin: e.target.value })}
                  rows={2}
                />
              </label>
              <label>
                Comentarios:
                <textarea
                  name="comentarios"
                  value={editForm.comentarios}
                  onChange={(e) => setEditForm({ ...editForm, comentarios: e.target.value })}
                  rows={2}
                />
              </label>
              {editError && <div style={{ color: "red", marginBottom: 8 }}>{editError}</div>}
              <div style={{ marginTop: "1.1rem" }}>
                <button type="submit">Guardar cambios</button>
                <button type="button" onClick={cerrarModalEditar} style={{ marginLeft: 8 }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MAPA (inicio + cierre + centro) */}
      {mostrarMapa && (ubicacionInicio || ubicacionCierre) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button onClick={() => setMostrarMapa(false)} className="modal-close">
              &times;
            </button>
            <h3>Ubicación del Ticaje</h3>
            <MapContainer
              center={
                mapFocus === "cierre"
                  ? (ubicacionCierre ? [ubicacionCierre.lat, ubicacionCierre.lng]
                    : (ubicacionInicio ? [ubicacionInicio.lat, ubicacionInicio.lng] : [0, 0]))
                  : (ubicacionInicio ? [ubicacionInicio.lat, ubicacionInicio.lng]
                    : (ubicacionCierre ? [ubicacionCierre.lat, ubicacionCierre.lng] : [0, 0]))
              }
              zoom={15}
              style={{ height: "400px", width: "100%" }}
            >
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Satélite + Callejero">
                  <TileLayer
                    url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}`}
                    attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Callejero">
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>

              {/* Marcador INICIO */}
              {ubicacionInicio && (
                <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                  <Tooltip direction="top" offset={[0, -10]}>📍 Ticaje (inicio)</Tooltip>
                </Marker>
              )}

              {/* Marcador CIERRE (verde) */}
              {ubicacionCierre && (
                <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                  <Tooltip direction="top" offset={[0, -10]}>✅ Ticaje (cierre)</Tooltip>
                </Marker>
              )}

              {/* Centro de trabajo */}
              {ubicacionCentro && (
                <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                  <Tooltip direction="top" offset={[0, -10]}>🏢 Centro de trabajo</Tooltip>
                </Marker>
              )}
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
};
